import React, { useState, useEffect } from 'react';
import { X, Coins, CheckCircle, Loader2, Star, CreditCard, Zap } from 'lucide-react';
import { 
  getCreditPackages, 
  createCheckoutSession, 
  formatPrice 
} from '../services/paymentService';
import { CreditPackage } from '../types';

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentCredits: number;
}

const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({ isOpen, onClose, currentCredits }) => {
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadPackages();
    }
  }, [isOpen]);

  const loadPackages = async () => {
    setLoading(true);
    try {
      const pkgs = await getCreditPackages();
      setPackages(pkgs);
    } catch (err) {
      setError('Erro ao carregar pacotes.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (packageId: string) => {
    setPurchasing(packageId);
    setError('');

    try {
      const checkoutUrl = await createCheckoutSession(packageId);
      
      if (checkoutUrl) {
        // Redirect to Stripe Checkout
        window.location.href = checkoutUrl;
      } else {
        setError('Erro ao criar sessão de pagamento.');
      }
    } catch (err) {
      setError('Erro ao processar pagamento. Tente novamente.');
    } finally {
      setPurchasing(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-slate-900 rounded-2xl border border-slate-800 shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Coins className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Comprar Créditos</h2>
                <p className="text-sm text-slate-400">Saldo atual: {currentCredits} créditos</p>
              </div>
            </div>
            <button 
              onClick={onClose}
              className="p-2 hover:bg-slate-800 rounded-lg transition-colors text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-400/10 border border-red-400/20 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {packages.map((pkg) => (
                  <div 
                    key={pkg.id}
                    className={`relative p-5 rounded-xl border transition-all ${
                      pkg.popular 
                        ? 'bg-gradient-to-br from-emerald-900/50 to-slate-900 border-emerald-500/50' 
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    {pkg.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500 text-white text-xs font-bold rounded-full">
                          <Star className="w-3 h-3" /> Mais Popular
                        </span>
                      </div>
                    )}

                    <div className="text-center mb-4">
                      <div className="inline-flex items-center gap-2 text-3xl font-bold text-white">
                        <Zap className="w-8 h-8 text-yellow-400" />
                        {pkg.credits}
                      </div>
                      <p className="text-slate-400 text-sm">créditos</p>
                    </div>

                    <div className="text-center mb-4">
                      <span className="text-2xl font-bold text-white">{formatPrice(pkg.price)}</span>
                      <p className="text-xs text-slate-500">
                        {formatPrice(pkg.price / pkg.credits)} por crédito
                      </p>
                    </div>

                    <button
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={purchasing !== null}
                      className={`w-full py-2.5 rounded-lg font-medium transition-all flex items-center justify-center gap-2 ${
                        pkg.popular
                          ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      {purchasing === pkg.id ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processando...
                        </>
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Comprar
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>

              {/* Payment Methods Info */}
              <div className="mt-6 p-4 bg-slate-800/50 rounded-lg">
                <div className="flex items-center gap-3 text-sm text-slate-400">
                  <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0" />
                  <span>Pagamento seguro via Stripe. Aceitamos cartão de crédito, PIX e boleto.</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuyCreditsModal;

